import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type RowData,
} from '@tanstack/react-table';
import { cn } from '@/utils/helpers/classnames';
import TableSkeleton from '@/components/ui/TableSkeleton';

export interface TanStackTableColumnMeta {
  headerClassName?: string;
  cellClassName?: string;
}

interface TanStackTableProps<TData extends RowData> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  getRowId?: (row: TData, index: number) => string;
  onRowClick?: (row: TData) => void;
  rowClassName?: string | ((row: TData) => string);
  className?: string;
  tableClassName?: string;
  theadClassName?: string;
  tbodyClassName?: string;
  emptyState?: React.ReactNode;
  emptyMessage?: string;
  emptyClassName?: string;
  loading?: boolean;
}

export function TanStackTable<TData extends RowData>({
  data,
  columns,
  getRowId,
  onRowClick,
  rowClassName,
  loading,
  className = '',
  tableClassName = 'min-w-full divide-y divide-gray-200',
  theadClassName = 'bg-white',
  tbodyClassName = 'bg-white divide-y divide-gray-200',
  emptyState,
  emptyMessage = 'No data found',
  emptyClassName = 'px-6 py-12 text-center text-sm text-gray-500',
}: TanStackTableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className={className}>
      <table className={tableClassName}>
        <thead className={theadClassName}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = (header.column.columnDef.meta || {}) as TanStackTableColumnMeta;
                return (
                  <th
                    key={header.id}
                    scope="col"
                    className={cn('px-4 py-3 text-left text-sm font-semibold  uppercase tracking-wider', meta.headerClassName)}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className={tbodyClassName}>
          {loading ? (
                <tr>
                  <td colSpan={table.getHeaderGroups()[0].headers.length}>
                    <TableSkeleton />
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={emptyClassName}>
                {emptyState || emptyMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => {
              const computedRowClassName =
                typeof rowClassName === 'function'
                  ? rowClassName(row.original)
                  : rowClassName || '';
              const isClickable = Boolean(onRowClick);

              return (
                <tr
                  key={row.id}
                  className={`${computedRowClassName} ${isClickable ? 'cursor-pointer' : ''}`.trim()}
                  onClick={isClickable ? () => onRowClick?.(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = (cell.column.columnDef.meta || {}) as TanStackTableColumnMeta;
                    return (
                      <td
                        key={cell.id}
                        className={cn(meta.cellClassName, 'px-4 py-2 whitespace-nowrap text-sm text-gray-700')}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export type { ColumnDef } from '@tanstack/react-table';
